import axios from "../../../axios";
import { createAsyncThunk } from "@reduxjs/toolkit";
import urls from "../../../Utils/urls";
import moment from "moment";
import { formatNumbers } from "../../../Utils";

// Constants
const AUTH_HEADER = "Basic UkVTVEFQSUxPQU5MSU5ROjg4V2VIZWxwVSQjQA==";
const DATE_FORMAT = "MM-DD-yyyy";
const DEFAULT_BORROWER_NAME = "Unknown";

/**
 * Formats a date using moment.js
 * @param {string|Date|null} date - The date to format
 * @returns {string} Formatted date string or "N/A"
 */
const formatDate = (date) => {
  return date ? moment(new Date(date)).format(DATE_FORMAT) : "N/A";
};

/**
 * Safely formats a number to currency format
 * @param {number|string} value - The value to format
 * @returns {string} Formatted number string
 */
const formatAmount = (value) => {
  return formatNumbers(Math.abs(Number(value ?? 0)));
};

const formatAmountIncludingNSFFee = (value1, value2) => {
  const amount = Math.abs(Number(value1)) + 45 + Math.abs(Number(value2));
  return formatNumbers(Math.abs(Number(amount ?? 0)));
};

const formatAmountInNumberIncludingNSFFee = (value1, value2) => {
  const amount = Math.abs(Number(value1)) + 45 + Math.abs(Number(value2));
  return Math.abs(Number(amount ?? 0));
};

/**
 * Generates a unique ID for NSF records
 * @param {Object} record - The record object
 * @param {number} index - Fallback index
 * @param {string} dateField - Date field to use in ID
 * @returns {string} Unique ID
 */
const generateRecordId = (record, index, dateField = "failureDate") => {
  const identifier = record?.LoanRecID || record?.LoanNumber || index;
  const date = record?.[dateField] || record?.DateDue || index;
  return `${identifier}-${date}${index ? `-${index}` : ""}`;
};

/**
 * Normalizes NSF record data with consistent formatting
 * @param {Object} record - Raw NSF record
 * @param {number} index - Record index
 * @returns {Object} Normalized record
 */
const normalizeNsfRecord = (record, index) => {
  const rawFailureDate = record?.failureDate || record?.DateDue || null;
  const rawNextDueDate = record?.nextDueDate || record?.DateDue || null;

  return {
    id: generateRecordId(record, index),
    borrower: record?.borrowerName?.trim() || DEFAULT_BORROWER_NAME,
    date: rawFailureDate,
    dateFormatted: formatDate(rawFailureDate),
    amount: formatAmount(record?.Amount),
    amountIncludingNSFFee: formatAmountIncludingNSFFee(
      record?.Amount,
      record?.LateCharges,
    ),
    numberAmountIncludingNSFFee: formatAmountInNumberIncludingNSFFee(
      record?.Amount,
      record?.LateCharges,
    ),
    rawAmount: record?.Amount,
    loanNumber: record?.LoanNumber || "",
    accountNumber: record?.accountLast4 || "",
    nextDueDate: rawNextDueDate,
    nextDueDateFormatted: formatDate(rawNextDueDate),
    nsfFee: formatAmount(record?.nsfFee),
    daysOverDue: record?.["Days Overdue"] || "",
    LoanRecID: record?.LoanRecID,
    ...record,
  };
};

/**
 * Normalizes NSF account data for cases
 * @param {Object} account - Raw NSF account
 * @param {number} index - Account index
 * @returns {Object} Normalized account
 */
const normalizeNsfAccount = (account, index) => {
  const rawFailureDate = account?.failureDate || account?.DateDue || null;
  const rawNextDueDate = account?.nextDueDate || account?.DateDue || null;

  return {
    id: generateRecordId(account, index),
    borrower: account?.borrowerName?.trim() || DEFAULT_BORROWER_NAME,
    date: rawFailureDate,
    dateFormatted: formatDate(rawFailureDate),
    amount: formatAmount(account?.Amount),
    amountIncludingNSFFee: formatAmountIncludingNSFFee(
      account?.Amount,
      account?.LateCharges,
    ),
    numberAmountIncludingNSFFee: formatAmountInNumberIncludingNSFFee(
      account?.Amount,
      account?.LateCharges,
    ),
    rawAmount: account?.Amount,
    loanNumber: account?.account || account?.LoanNumber || "",
    accountNumber: account?.accountLast4 || "",
    nextDueDate: rawNextDueDate,
    nextDueDateFormatted: formatDate(rawNextDueDate),
    nsfFee: formatAmount(account?.nsfFee) || 0,
    daysOverDue: account?.["Days Overdue"] || "",
    LoanRecID: account?.LoanRecID,
    ...account,
  };
};

/**
 * Parses raw NSF data from API response
 * @param {Array} rawData - Raw data array from API
 * @returns {Object|null} Parsed NSF data or null
 */
const parseNsfData = (rawData) => {
  if (!Array.isArray(rawData) || rawData.length === 0) {
    return null;
  }

  try {
    const jsonString = rawData.map((row) => Object.values(row)[0]).join("");
    return JSON.parse(jsonString);
  } catch (error) {
    console.error("Failed to parse NSF JSON:", error);
    return null;
  }
};

/**
 * Filters NSF accounts by excluding those present in NSF_DATA
 * @param {Array} nsfAccounts - NSF accounts to filter
 * @param {Array} nsfData - NSF data to compare against
 * @returns {Array} Filtered NSF accounts
 */
const filterNsfAccounts = (nsfAccounts, nsfData) => {
  if (!Array.isArray(nsfAccounts) || !Array.isArray(nsfData)) {
    return nsfAccounts || [];
  }

  const nsfDataLoanNumbers = new Set(
    nsfData.map((item) => item.LOAN_ACCOUNT_NUMBER),
  );

  return nsfAccounts.filter(
    (account) => !nsfDataLoanNumbers.has(account.account),
  );
};

/**
 * Calculates summary statistics for NSF accounts
 * @param {Array} nsfAccounts - Array of NSF accounts
 * @returns {Object|null} Summary object or null
 */
const calculateSummary = (nsfAccounts) => {
  if (!Array.isArray(nsfAccounts) || nsfAccounts.length === 0) {
    return null;
  }

  const totalAmount = nsfAccounts.reduce(
    (sum, { Amount = 0 }) => sum + Number(Amount),
    0,
  );

  return {
    TotalNSFAmount: totalAmount.toFixed(2),
    totalRecords: nsfAccounts.length,
  };
};

/**
 * Groups normalized history by LoanRecID for quick lookup
 * @param {Array} history - Normalized history array
 * @returns {Object} Grouped history by LoanRecID
 */
const groupHistoryByLoanRecId = (history) => {
  return history.reduce((acc, record) => {
    const loanRecId = record.LoanRecID;
    if (loanRecId) {
      if (!acc[loanRecId]) {
        acc[loanRecId] = [];
      }
      acc[loanRecId].push(record);
    }
    return acc;
  }, {});
};

/**
 * Makes API calls to fetch NSF data
 * @param {string} accounts - Account parameter
 * @param {Array} accountsNumber - Account numbers array
 * @returns {Promise<Array>} Array containing both API responses
 */
const fetchNsfApiData = async (accounts, accountsNumber) => {
  const [response1, response2] = await Promise.all([
    axios.get(urls.borrower.nsfNotificationsDashboard(accounts)),
    axios.post(
      urls.borrower.nsfCheckDetails(accounts),
      { loanAccounts: accountsNumber },
      {
        headers: {
          Authorization: AUTH_HEADER,
        },
      },
    ),
  ]);

  return [response1, response2];
};

/**
 * Fetches and processes NSF data
 * @param {Object} params - Parameters object
 * @param {string} params.accounts - Account parameter
 * @param {Array} params.accountsNumber - Account numbers array
 * @returns {Promise<Object>} Processed NSF data
 */
export const fetchNsfData = createAsyncThunk(
  "nsf/fetchNsfData",
  async ({ accounts, accountsNumber }) => {
    try {
      // Fetch data from both endpoints
      const [response1, response2] = await fetchNsfApiData(
        accounts,
        accountsNumber,
      );

      // Parse the main NSF data
      const rawData = response1?.data?.[0] ?? [];
      const topLevel = parseNsfData(rawData);

      if (!topLevel) {
        throw new Error("Failed to parse NSF data");
      }

      // Filter NSF accounts
      const filteredNsfAccounts = filterNsfAccounts(
        topLevel.nsfAccount,
        response2?.data?.NSF_DATA,
      );

      // Update topLevel with filtered accounts
      const processedTopLevel = {
        ...topLevel,
        nsfAccount: filteredNsfAccounts,
      };

      // Calculate summary
      const summary = calculateSummary(filteredNsfAccounts);

      // Process history data
      const history = processedTopLevel.history || [];
      const normalizedHistory = history.map(normalizeNsfRecord);

      // Group history by LoanRecID
      const grouped = groupHistoryByLoanRecId(normalizedHistory);

      // Process cases from NSF accounts
      const cases = filteredNsfAccounts.map(normalizeNsfAccount);

      // Get count (assuming it's the second element if topLevel is an array)
      const count = Array.isArray(topLevel) ? topLevel[1] : null;

      return {
        ...(summary || {}),
        cases,
        grouped,
        count,
        nsfAccount: filteredNsfAccounts,
      };
    } catch (error) {
      console.error("Error fetching NSF data:", error);
      throw error;
    }
  },
);

/**
 * Fetches NSF history data without date filtering
 * @param {Object} params - Parameters object
 * @param {string} params.accounts - Account parameter
 * @returns {Promise<Object>} Processed NSF history data
 */
export const fetchNsfHistory = createAsyncThunk(
  "nsf/fetchNsfHistory",
  async ({ accounts }) => {
    try {
      const response = await axios.get(
        urls.borrower.nsfHistoryWithoutDate(accounts),
      );

      // Parse the history data
      const rawData = response?.data?.[0] ?? [];
      const parsedData = parseNsfData(rawData);

      if (!parsedData) {
        return {
          cases: [],
          grouped: {},
          totalRecords: 0,
        };
      }

      // Process history records
      const history = parsedData.history || [];
      const normalizedHistory = history.map(normalizeNsfRecord);

      // Group history by LoanRecID for modal display
      const grouped = groupHistoryByLoanRecId(normalizedHistory);

      // Create unique cases (one per loan/borrower combination)
      // Group by LoanRecID and take the most recent NSF for each loan
      const casesMap = new Map();

      normalizedHistory.forEach((record) => {
        const key =
          record.LoanRecID || `${record.loanNumber}-${record.borrower}`;
        const existing = casesMap.get(key);

        if (!existing) {
          casesMap.set(key, record);
        } else {
          // Keep the most recent record
          const existingDate = new Date(existing.date || "1900-01-01");
          const currentDate = new Date(record.date || "1900-01-01");
          if (currentDate > existingDate) {
            casesMap.set(key, record);
          }
        }
      });

      // Convert map to array and sort by date (newest first)
      const cases = Array.from(casesMap.values()).sort((a, b) => {
        const dateA = new Date(a.date || "1900-01-01");
        const dateB = new Date(b.date || "1900-01-01");
        return dateB - dateA;
      });

      return {
        cases,
        grouped,
        totalRecords: normalizedHistory.length,
      };
    } catch (error) {
      console.error("Error fetching NSF history:", error);
      throw error;
    }
  },
);
